import Seo from "@/components/Seo";
import Header from "@/components/landing/Header";
import IndustryStrip from "@/components/landing/IndustryStrip";
import Marquee from "@/components/landing/Marquee";
import LeadForm from "@/components/landing/LeadForm";
import Footer from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { scrollToId } from "@/lib/scroll";
import { Check, X, Minus, ArrowRight } from "lucide-react";

const rows = [
  { feature: "Response time", theirs: "Minutes to hours", ours: "8 seconds", winner: true },
  { feature: "Cost", theirs: "$300–600/mo plus per-minute fees", ours: "Flat rate from $349/mo", winner: true },
  { feature: "How they sound", theirs: "Generic script, knows nothing about trades", ours: "Your branding, your services, your rules", winner: true },
  { feature: "What the caller gets", theirs: "A message taken", ours: "A qualified, booked job", winner: true },
  { feature: "After-hours coverage", theirs: "Available, often with surcharges", ours: "Included, every night and weekend", winner: true },
  { feature: "Emergency handling", theirs: "Reads from a script", ours: "Instant escalation to your on-call phone", winner: true },
  { feature: "Forgotten estimates", theirs: "Not covered", ours: "Automatic follow-up campaigns", winner: true },
  { feature: "Where your data lives", theirs: "Their portal, their format", ours: "Your own dashboard plus a weekly plain-English report", winner: true },
  { feature: "Hiring or training", theirs: "None needed", ours: "None needed", winner: false },
];

const Mark = ({ win }) =>
  win ? (
    <Check className="h-4 w-4 shrink-0 text-emerald-400" />
  ) : (
    <Minus className="h-4 w-4 shrink-0 text-slate-500" />
  );

export default function Compare() {
  return (
    <>
      <Seo
        title="Backfill vs Answering Services — For Contractors"
        description="Compare Backfill's instant text-back recovery with traditional answering services: cost, speed, booking, and control for HVAC, plumbing, electrical and roofing companies."
      />
      <Header />
      <IndustryStrip />
      <main>
        <section data-testid="compare-hero" className="noise relative overflow-hidden pt-20 pb-16 sm:pt-24">
          <div className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]" />
          <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
            <Reveal>
              <Eyebrow>The Honest Comparison</Eyebrow>
              <h1 className="mt-5 font-display text-4xl font-bold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
                Answering services answer.{" "}
                <span className="text-[#F25F22]">Backfill books.</span>
              </h1>
              <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
                A traditional answering service takes a message you still have
                to chase. Backfill turns the same missed call into a qualified,
                booked job — automatically.
              </p>
            </Reveal>
          </div>
        </section>

        <section data-testid="compare-table-section" className="pb-24">
          <div className="mx-auto max-w-4xl px-5 sm:px-8">
            <Reveal>
              <div className="overflow-hidden rounded-xl border border-white/[0.08] bg-[#12161F]">
                <div className="grid grid-cols-[1.1fr_1fr_1fr] border-b border-white/[0.08] bg-white/[0.02] px-5 py-4 sm:px-7">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500"></span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                    Answering service
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#F25F22]">
                    Backfill
                  </span>
                </div>
                {rows.map((r, i) => (
                  <div
                    key={r.feature}
                    data-testid={`compare-row-${i}`}
                    className="grid grid-cols-[1.1fr_1fr_1fr] items-center gap-2 border-b border-white/[0.05] px-5 py-4 last:border-0 sm:px-7"
                  >
                    <span className="text-sm font-medium text-slate-200">{r.feature}</span>
                    <span className="flex items-start gap-2 text-xs leading-relaxed text-slate-500 sm:text-sm">
                      {r.winner ? <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-600" /> : <Mark win={false} />}
                      {r.theirs}
                    </span>
                    <span className="flex items-start gap-2 text-xs font-medium leading-relaxed text-slate-100 sm:text-sm">
                      <Mark win />
                      {r.ours}
                    </span>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1} className="mt-12 text-center">
              <p className="mx-auto max-w-lg text-base text-slate-400">
                One recovered emergency job usually pays for a month of
                Backfill. The calculator on the homepage does the math with
                your numbers.
              </p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
                <a
                  href="#demo"
                  data-testid="compare-demo-cta"
                  onClick={(e) => { e.preventDefault(); scrollToId("#demo"); }}
                  className="group flex items-center gap-2 rounded-md bg-[#F25F22] px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#FF7539] hover:shadow-[0_0_28px_rgba(242,95,34,0.45)]"
                >
                  Book a Demo
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </a>
                <a
                  href="/"
                  data-testid="compare-home-cta"
                  className="rounded-md border border-white/[0.12] bg-white/[0.03] px-6 py-3.5 text-sm font-semibold text-slate-200 transition-all hover:border-[#F25F22]/50"
                >
                  See the Full Product
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        <Marquee />
        <LeadForm source="compare" />
      </main>
      <Footer />
    </>
  );
}
